// The Admin tab's wire types and calls — /api/admin-console plus the two settings routes it
// still shares with the rest of the app.
//
// Every composite read here follows the degradation contract (F5.1): it carries `problems[]`
// and the page renders the source's own message rather than a blank zone. The narrated
// consequence line arrives as typed PIECES, not markup — the server composes the sentence, the
// page renders each piece in the voice ATTEST assigns it (mono for a checkable ref, tabular
// sans for a quantity, serif for the record's own words).

import { request } from './client.js';
import type { Problem } from '../../shared/problems.js';
import type { RailRoute } from '../../shared/rail.js';

// ── the sentence's pieces (mirrors src/server/utils/auditNarrate.ts) ────────

export type Piece =
  | { k: 'txt'; v: string }
  | { k: 'ref'; v: string }
  | { k: 'fig'; v: string }
  | { k: 'quote'; v: string }
  | { k: 'em'; v: string; tone?: 'attend' | 'broken' }
  | { k: 'none' };

// ── posture ────────────────────────────────────────────────────────────────

export interface AllowedEmail { id: number; email: string; is_admin: boolean; created_at: string }

export interface OperatorAccount {
  id: number;
  name: string;
  email: string | null;
  initials: string;
  is_admin: boolean;
  /** `password_hash IS NOT NULL`. The hash itself never leaves the database. */
  has_password: boolean;
  created_at: string;
}

export interface TokenTally {
  total: number; live: number; revoked: number; last_expiry: string | null;
  /** False when the tally could not be computed — an UNKNOWN, which is not a PASS (B8). */
  known: boolean;
}

export interface SettingRow {
  key: string;
  label: string;
  default: string;
  value: string;
  /** 'file.ts:line' for every site that reads it. Empty means nothing reads it. */
  consumers: string[];
  dead_note: string | null;
}

export interface Posture {
  registration_mode: string | null;
  boot_reasserts_allowlist: boolean;
  agent_token_shape: string;
  allowed_emails: AllowedEmail[];
  users: OperatorAccount[];
  agent_tokens: TokenTally;
  portal: { users: number; clients: number; known: boolean };
  settings: SettingRow[];
  audit: { total: number; portal_total: number; known: boolean };
  problems: Problem[];
  generated_at: string;
}

// ── vocabulary, day band, consequence tallies ──────────────────────────────

export interface Verb { action: string; resource_type?: string; n: number; last: string }
export interface DayBucket { d: string; n: number }

export interface Vocabulary {
  realm: 'operator' | 'client';
  verbs: Verb[];
  days: DayBucket[];
  total: number;
  /** How many rows carry an IP. Zero on the operator trail, by construction. */
  with_ip: number;
  notes: { authored: number; templated_rows: number; templates: number } | null;
  classes: {
    tomb: number; hash: number;
    client_deletes: number; engagement_deletes: number; tomb_chars: number;
  } | null;
  problems: Problem[];
  generated_at: string;
}

// ── the trail ──────────────────────────────────────────────────────────────

export type ConsequenceClass = 'reason' | 'tomb' | 'hash';

export interface TrailRow {
  id: number;
  action: string;
  resource_type: string;
  resource_id: string | null;
  created_at: string;
  /** Null means the machine wrote it — rendered *system · agent*, never a dash. */
  actor: string | null;
  pieces: Piece[];
  line: string;
  klass: ConsequenceClass | null;
  route: RailRoute;
  /** Why there is no route. Shown, never swallowed. */
  route_reason: string | null;
  citation: string;
  note: { text: string; authored: boolean; multiplicity: number } | null;
  details: Record<string, unknown> | null;
}

export interface TrailPage {
  realm: 'operator' | 'client';
  rows: TrailRow[];
  total: number;
  page: number;
  limit: number;
  problems: Problem[];
  generated_at: string;
}

export interface TrailQuery {
  realm?: 'operator' | 'client';
  action?: string | null;
  family?: string | null;
  day?: string | null;
  class?: ConsequenceClass | null;
  search?: string | null;
  /** `#admin?tab=audit&engagement=N` — the arrival intent Lifecycle's ledger routes into. */
  engagement?: number | null;
  page?: number;
  limit?: number;
  /** Initial load only — the one read that records itself. Never sent by a poll. */
  first?: boolean;
}

const qs = (q: TrailQuery): string => {
  const p = new URLSearchParams();
  if (q.realm) p.set('realm', q.realm);
  if (q.action) p.set('action', q.action);
  if (q.family) p.set('family', q.family);
  if (q.day) p.set('day', q.day);
  if (q.class) p.set('class', q.class);
  if (q.search) p.set('search', q.search);
  if (q.engagement) p.set('engagement', String(q.engagement));
  if (q.page && q.page > 1) p.set('page', String(q.page));
  if (q.limit) p.set('limit', String(q.limit));
  if (q.first) p.set('first', '1');
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const adminConsoleApi = {
  posture: () => request<Posture>('/admin-console/posture'),
  vocabulary: (realm: 'operator' | 'client' = 'operator') =>
    request<Vocabulary>(`/admin-console/vocabulary?realm=${realm}`),
  trail: (q: TrailQuery) => request<TrailPage>(`/admin-console/trail${qs(q)}`),
  /** The export is a navigation, not a fetch — the browser owns the download. */
  exportHref: (q: TrailQuery) => `/api/admin-console/export.csv${qs({ ...q, page: undefined, first: undefined })}`,

  admitEmail: (email: string) =>
    request<AllowedEmail>('/admin-console/allowlist', { method: 'POST', body: JSON.stringify({ email }) }),
  withdrawEmail: (id: number) =>
    request<{ ok: true }>(`/admin-console/allowlist/${id}`, { method: 'DELETE' }),
  setRegistrationMode: (mode: 'allowlist' | 'open') =>
    request<{ mode: string }>('/admin-console/registration-mode', { method: 'PUT', body: JSON.stringify({ mode }) }),
  revokeAllTokens: () =>
    request<{ revoked: number }>('/admin-console/agent-tokens/revoke-all', { method: 'POST' }),

  /** The kit's own route — it verifies the current password with bcrypt and we do not re-do that. */
  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ success?: boolean }>('/admin/users/me/password', {
      method: 'PUT', body: JSON.stringify({ currentPassword, newPassword }),
    }),

  saveSettings: (changed: Record<string, string>) =>
    request<Record<string, string>>('/settings', { method: 'PUT', body: JSON.stringify(changed) }),
};
